import {
    ApplicationHelpType,
    AssertionDescriptorValueDisplayTypes,
    AssertionDescriptorValueSourceTypeEnum,
    CaseFileStaticItems,
    EntityTypes,
    UserRightsEnum,
} from '../../../utilities/Constants';
import {
    ICaseAssertionModel,
    ICaseModel,
    ICaseStatusLookupModel,
    IDepositionModel,
    IDocumentModel,
} from '../../../interfaces/ICase';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import { INoteCategoryTypeModel, INoteModel } from '../../../interfaces/INote';
import { JSX, useState } from 'react';

import Authorization from '../../../stores/Authorization';
import { CaseDepositionListWrapper } from '../static-sections/CaseDepositionListWrapper';
import { CaseExpertsList } from '../static-sections/case-experts/CaseExpertsList';
import CaseFileCheckmateSelectHelper from '../../../utilities/CaseFileCheckmateSelectHelper';
import CaseHelper from '../../../utilities/CaseHelper';
import CheckmateSelect from '../../shared/CheckmateSelect';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import { CurrencyWithCentsInputControl } from '../../../shared/controls/CurrencyWithCentsInput';
import { DateInputControl } from '../../../shared/controls/DateInputControl';
import { DocumentTypeEnum } from '../../../utilities/DbConstants';
import { DocumentsList } from '../../documents-list/DocumentsList';
import { Help } from '../../shared/Help';
import { IAssertionDescriptorModel } from '../../../interfaces/IAssertionDescriptor';
import { ICaseExpertModel } from '../../../interfaces/ICaseExpert';
import { IUserModel } from '../../../interfaces/IUser';
import { NotesRow } from './NotesRow';
import { Stack } from 'react-bootstrap';
import { WholeNumberInputControl } from '../../../shared/WholeNumberInputControl';
import Sort from '../../../stores/Sort';
import { cloneDeep } from 'lodash';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useZoneContext } from '../../../contexts/useZoneContext';

interface ICaseEasyUpdateFieldsTableRowProps {
    assertionDescriptors: IAssertionDescriptorModel[];
    caseStatuses: ICaseStatusLookupModel[];
    currentCase: ICaseModel;
    defenseCounsels: ILookupModel[];
    getValue: (item: IMultiSelectOption) => any;
    item: IMultiSelectOption;
    isSameCaseGroupPendingLockedField: boolean;
    isSameCaseGroupApprovedLockedField: boolean;
    noteCategoryTypes: INoteCategoryTypeModel[];
    onAddNote: () => void;
    onChange: (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>,
        item?: IMultiSelectOption
    ) => void;
    onDeleteFieldClick: (item: IMultiSelectOption, force?: boolean) => void;
    onDepositionsChange: (updatedDepositions: IDepositionModel[]) => void;
    onDocumentDeleteComplete: (documentGuid: string) => void;
    onDocumentSaveComplete: (documents: IDocumentModel[]) => void;
    onExpertsChanges: (caseExperts: ICaseExpertModel[]) => void;
    onInputError?: (item: IMultiSelectOption, errorMessage: string) => void;
    onMultiSelectValuesChanged: (optionsList: any, ad: IAssertionDescriptorModel) => void;
    onNarrativeFieldEdit: (item: IMultiSelectOption) => void;
    onNotesChanges: (notes: INoteModel[]) => void;
    onSearchableSingleSelectChange: (
        optionsList: any,
        assertionDescriptorGuid?: string,
        staticItemId?: number,
        isBoolean?: boolean
    ) => void;
    onSetCurrentCaseExpert: (caseExpert: ICaseExpertModel | null) => void;
    onViewEditNote: (noteGuidToEdit: string, readOnly?: boolean | undefined) => void;
    zoneUsers: IUserModel[];
}

export function CaseEasyUpdateFieldsTableRow(props: ICaseEasyUpdateFieldsTableRowProps) {
    const { item } = props;
    const zoneContext = useZoneContext();
    const { user } = useAuthContext();

    const [showDeposSection, setShowDeposSection] = useState<boolean>(false);
    const [showExpertsSection, setShowExpertsSection] = useState<boolean>(false);
    const [showNotesSection, setShowNotesSection] = useState<boolean>(false);
    const [showDocumentsSection, setShowDocumentsSection] = useState<boolean>(false);

    const getCriteriaHelpText = (item: IMultiSelectOption) => {
        const helpText = CaseHelper.getAssertionDescriptorFieldHelpText(
            props.assertionDescriptors,
            item.value,
            item.guid,
            false,
            props.isSameCaseGroupPendingLockedField,
            props.isSameCaseGroupApprovedLockedField
        );
        if (helpText)
            return <Help type={ApplicationHelpType.Info} title={item.label} helpText={helpText} />;
        else return null;
    };

    const buildCaseFieldValues = (item: IMultiSelectOption): any => {
        const content = [];

        let caseAssertion: ICaseAssertionModel | undefined = props.currentCase.caseAssertions?.find(
            (x) => x.assertionDescriptorGuid === item.guid
        ) ?? { assertionDescriptorGuid: item.guid };

        const setStyleAsSystemAutoPopulated = CaseHelper.setStyleAsSystemAutoPopulated(
            caseAssertion,
            props.currentCase
        );
        const additionalStyleClass = setStyleAsSystemAutoPopulated ? ' font-green' : '';
        const readOnly = CaseHelper.isFieldReadOnly(item.guid ?? '', props.currentCase);

        const assertionDescriptorMatch = props.assertionDescriptors.find(
            (x) => x.guid?.toUpperCase() === item.guid?.toUpperCase()
        );

        if (assertionDescriptorMatch) {
            if (readOnly) {
                const value = props.getValue(item);
                let text = '';
                if (
                    assertionDescriptorMatch.valueSourceType!.id ===
                    AssertionDescriptorValueSourceTypeEnum.Selection.Value
                ) {
                    if (value) {
                        if (assertionDescriptorMatch.assertionDescriptorValues) {
                            const advMatch =
                                assertionDescriptorMatch.assertionDescriptorValues.find(
                                    (x) => x.guid === value.guid
                                );
                            if (advMatch) text = advMatch.text || '';
                            else {
                                console.log(
                                    'Unexpected Error - No ADV Match for AD.Guid = ' +
                                        assertionDescriptorMatch.guid +
                                        '; ADV Guid = ' +
                                        JSON.stringify(value)
                                );
                            }
                        }
                    }
                } else {
                    text = value;
                }
                content.push(
                    <span>
                        <input
                            type="text"
                            className={'form-control' + additionalStyleClass}
                            style={{ width: '42%' }}
                            value={text}
                            readOnly={true}
                        />
                        <span className="text-danger" style={{ marginTop: 3 }}>
                            {item.validationError}
                        </span>
                    </span>
                );
            } else {
                switch (assertionDescriptorMatch.valueSourceType!.id) {
                    case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                        if (
                            assertionDescriptorMatch.assertionDescriptorValues &&
                            assertionDescriptorMatch.assertionDescriptorValues.length > 0
                        ) {
                            content.push(
                                <>
                                    {assertionDescriptorMatch.valueDisplayType?.id ===
                                    AssertionDescriptorValueDisplayTypes.MultiSelect.Value ? (
                                        <CheckmateSelect
                                            options={CaseHelper.getMultiSelectOptions(
                                                assertionDescriptorMatch
                                            )}
                                            value={props.getValue(item)}
                                            systemPopulated={setStyleAsSystemAutoPopulated}
                                            placeholder="Select"
                                            isMulti={true}
                                            onChange={(e: any) => {
                                                props.onMultiSelectValuesChanged(
                                                    e,
                                                    assertionDescriptorMatch
                                                );
                                            }}
                                        />
                                    ) : (
                                        <CheckmateSelect
                                            options={CaseFileCheckmateSelectHelper.getOptionsForCheckmateSelect(
                                                assertionDescriptorMatch.assertionDescriptorValues!
                                            )}
                                            value={props.getValue(item)}
                                            systemPopulated={setStyleAsSystemAutoPopulated}
                                            onChange={(e: any) => {
                                                props.onSearchableSingleSelectChange(
                                                    e,
                                                    assertionDescriptorMatch!.guid!
                                                );
                                            }}
                                            isDisabled={
                                                props.isSameCaseGroupPendingLockedField ||
                                                props.isSameCaseGroupApprovedLockedField
                                            }
                                        />
                                    )}
                                    <span className="text-danger">{item.validationError}</span>
                                </>
                            );
                        }
                        break;
                    case AssertionDescriptorValueSourceTypeEnum.Text.Value: {
                        const valueDisplayTypeId = assertionDescriptorMatch.valueDisplayType
                            ? assertionDescriptorMatch.valueDisplayType.id
                            : 0;

                        switch (valueDisplayTypeId) {
                            case AssertionDescriptorValueDisplayTypes.Currency.Value:
                                content.push(
                                    <Stack direction="horizontal" gap={1}>
                                        <span>$&nbsp;</span>
                                        <WholeNumberInputControl
                                            name="numericValue"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                props.onChange(e, item);
                                            }}
                                            placeholder="Enter a valid $ amount"
                                            style={{ display: 'inline-block' }}
                                            value={props.getValue(item)}
                                        />
                                        <span className="text-danger">{item.validationError}</span>
                                    </Stack>
                                );
                                break;
                            case AssertionDescriptorValueDisplayTypes.CurrencyWithCents.Value:
                                content.push(
                                    <>
                                        <CurrencyWithCentsInputControl
                                            name="numericValue"
                                            onChange={(e) => {
                                                props.onChange(e, item);
                                            }}
                                            placeholder="Enter a valid $ amount"
                                            style={{ display: 'inline-block' }}
                                            value={props.getValue(item)}
                                        />
                                        <span className="text-danger">{item.validationError}</span>
                                    </>
                                );
                                break;
                            case AssertionDescriptorValueDisplayTypes.DateTime.Value: {
                                const dateTimeValue = props.getValue(item);
                                content.push(
                                    <>
                                        <DateInputControl
                                            additionalStyleClass={additionalStyleClass}
                                            item={item}
                                            onChange={props.onChange}
                                            onInputError={props.onInputError}
                                            value={dateTimeValue}
                                        />
                                        <span className="text-danger" style={{ marginTop: 3 }}>
                                            {item.validationError}
                                        </span>
                                    </>
                                );
                                break;
                            }
                            case AssertionDescriptorValueDisplayTypes.Narrative.Value:
                                content.push(
                                    <>
                                        <button
                                            className="btn btn-no-bg"
                                            onClick={() => props.onNarrativeFieldEdit(item)}
                                        >
                                            {caseAssertion.textValue && (
                                                <i className="fal fa-pen color-black" />
                                            )}
                                            {!caseAssertion.textValue && (
                                                <i className="fal fa-pen text-gray" />
                                            )}
                                        </button>
                                        <span className="text-danger">{item.validationError}</span>
                                    </>
                                );
                                break;
                            case AssertionDescriptorValueDisplayTypes.Number.Value:
                            case AssertionDescriptorValueDisplayTypes.Decimal.Value:
                                content.push(
                                    <>
                                        <input
                                            type="number"
                                            className={'form-control' + additionalStyleClass}
                                            style={{ width: '42%' }}
                                            value={props.getValue(item)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                props.onChange(e, item);
                                            }}
                                        />
                                        <span className="text-danger">{item.validationError}</span>
                                    </>
                                );
                                break;
                            case AssertionDescriptorValueDisplayTypes.SSN.Value:
                                content.push(
                                    <>
                                        <input
                                            type="text"
                                            className={
                                                'form-control input-SSN ' + additionalStyleClass
                                            }
                                            maxLength={11}
                                            placeholder="XXX-XX-XXXX"
                                            value={props.getValue(item)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                props.onChange(e, item);
                                            }}
                                        />
                                        <span className="text-danger">{item.validationError}</span>
                                    </>
                                );
                                break;
                            default:
                                content.push(
                                    <>
                                        <input
                                            type="text"
                                            className={'form-control' + additionalStyleClass}
                                            style={{ width: '42%' }}
                                            value={props.getValue(item)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                props.onChange(e, item);
                                            }}
                                            disabled={
                                                props.isSameCaseGroupPendingLockedField ||
                                                props.isSameCaseGroupApprovedLockedField
                                            }
                                        />
                                        <span className="text-danger">{item.validationError}</span>
                                    </>
                                );
                                break;
                        }
                        break;
                    }
                    case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                        content.push(
                            <>
                                <div className="w-25">
                                    <CheckmateSelect
                                        options={CheckmateSelectHelper.getBooleanOptions()}
                                        value={props.getValue(item)}
                                        systemPopulated={setStyleAsSystemAutoPopulated}
                                        onChange={(e: any) => {
                                            props.onSearchableSingleSelectChange(
                                                e,
                                                assertionDescriptorMatch!.guid!,
                                                undefined,
                                                true
                                            );
                                        }}
                                    />
                                </div>
                                <span className="text-danger">{item.validationError}</span>
                            </>
                        );
                        break;
                    default:
                        break;
                }
            }
        } else {
            switch (item.id) {
                case CaseFileStaticItems.CaseNumber.Id:
                case CaseFileStaticItems.UniqueCaseID.Id:
                    content.push(
                        <span>
                            <input
                                className="form-control"
                                style={{ width: '42%' }}
                                type="text"
                                name={item.value}
                                value={props.getValue(item)}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    props.onChange(e, item);
                                }}
                                disabled={
                                    props.isSameCaseGroupPendingLockedField ||
                                    props.isSameCaseGroupApprovedLockedField
                                }
                            />
                            <span className="text-danger">{item.validationError}</span>
                        </span>
                    );
                    break;
                case CaseFileStaticItems.CaseStatus.Id:
                    content.push(
                        <>
                            <CheckmateSelect
                                options={CaseFileCheckmateSelectHelper.getCaseStatusOptionsForCheckmateSelect(
                                    props.caseStatuses
                                )}
                                value={props.getValue(item)}
                                systemPopulated={setStyleAsSystemAutoPopulated}
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, undefined, item.id);
                                }}
                            />
                            <span className="text-danger">{item.validationError}</span>
                        </>
                    );
                    break;
                case CaseFileStaticItems.CaseManager.Id:
                case CaseFileStaticItems.AlternateCaseManager.Id:
                    content.push(
                        <>
                            <CheckmateSelect
                                options={CheckmateSelectHelper.getUserOptions(props.zoneUsers)}
                                value={props.getValue(item)}
                                systemPopulated={setStyleAsSystemAutoPopulated}
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, undefined, item.id);
                                }}
                            />
                            <span className="text-danger">{item.validationError}</span>
                        </>
                    );
                    break;
                case CaseFileStaticItems.LocalCounsel.Id:
                    content.push(
                        <>
                            <CheckmateSelect
                                options={CheckmateSelectHelper.getOrgOptions(props.defenseCounsels)}
                                value={props.getValue(item)}
                                systemPopulated={setStyleAsSystemAutoPopulated}
                                onChange={(e: any) => {
                                    props.onSearchableSingleSelectChange(e, undefined, item.id);
                                }}
                            />
                            <span className="text-danger">{item.validationError}</span>
                        </>
                    );
                    break;
                case CaseFileStaticItems.CasePriority.Id:
                    content.push(
                        <span>
                            <div className="w-25">
                                <CheckmateSelect
                                    options={CheckmateSelectHelper.getBooleanOptions()}
                                    value={props.getValue(item)}
                                    systemPopulated={setStyleAsSystemAutoPopulated}
                                    onChange={(e: any) => {
                                        props.onSearchableSingleSelectChange(
                                            e,
                                            undefined,
                                            item.id,
                                            true
                                        );
                                    }}
                                />
                            </div>
                            <span className="text-danger">{item.validationError}</span>
                        </span>
                    );
                    break;
                case CaseFileStaticItems.NotesSection.Id:
                    content.push(
                        <div>
                            <button
                                className="btn btn-no-bg text-gray"
                                onClick={handleToggleNotesSection}
                            >
                                <i className="fal fa-memo" />
                                &nbsp;{showNotesSection ? 'Close' : 'Open'}
                            </button>
                        </div>
                    );
                    break;
                case CaseFileStaticItems.DepositionSection.Id:
                    content.push(
                        <div>
                            <button
                                className="btn btn-no-bg text-gray"
                                onClick={handleToggleDeposSection}
                            >
                                <i className="fal fa-memo" />
                                &nbsp;{showDeposSection ? 'Close' : 'Open'}
                            </button>
                        </div>
                    );
                    break;
                case CaseFileStaticItems.ExpertsSection.Id:
                    content.push(
                        <div>
                            <button
                                className="btn btn-no-bg text-gray"
                                onClick={handleToggleExpertsSection}
                            >
                                <i className="fal fa-memo" />
                                &nbsp;{showExpertsSection ? 'Close' : 'Open'}
                            </button>
                        </div>
                    );
                    break;
                case CaseFileStaticItems.DocumentsSection.Id:
                    content.push(
                        <div>
                            <button
                                className="btn btn-no-bg text-gray"
                                onClick={handleToggleDocumentsSection}
                            >
                                <i className="fal fa-memo" />
                                &nbsp;{showExpertsSection ? 'Close' : 'Open'}
                            </button>
                        </div>
                    );
                    break;
            }
        }

        return content;
    };

    const handleToggleNotesSection = () => {
        setShowNotesSection(!showNotesSection);
    };

    const handleToggleDeposSection = () => {
        setShowDeposSection(!showDeposSection);
    };

    const handleToggleExpertsSection = () => {
        setShowExpertsSection(!showExpertsSection);
    };

    const handleToggleDocumentsSection = () => {
        setShowDocumentsSection(!showDocumentsSection);
    };

    const primaryRow = (
        <tr>
            <td>
                {item!.label}
                {getCriteriaHelpText(item)}
            </td>
            <td>{buildCaseFieldValues(item)}</td>
            <td width="10%">
                <button
                    className="btn btn-gray btn-icon float-end"
                    onClick={() => props.onDeleteFieldClick(item)}
                >
                    <i className="fal fa-times color-white" />
                </button>
            </td>
        </tr>
    );

    let secondaryRow: JSX.Element = <></>;

    switch (item.id) {
        case CaseFileStaticItems.NotesSection.Id:
            if (showNotesSection) {
                secondaryRow = (
                    <tr>
                        <NotesRow
                            user={user}
                            onAddNote={props.onAddNote}
                            onNotesChanges={props.onNotesChanges}
                            onViewEditNote={props.onViewEditNote}
                            notes={props.currentCase.notes ?? []}
                        />
                    </tr>
                );
            }

            break;
        case CaseFileStaticItems.DepositionSection.Id:
            if (showDeposSection) {
                secondaryRow = (
                    <tr>
                        <td colSpan={3}>
                            <CaseDepositionListWrapper
                                user={user}
                                caseGuid={props.currentCase.guid ?? ''}
                                usersInZone={props.zoneUsers ?? []}
                                allDefenseCounselsForZone={props.defenseCounsels}
                                depositions={props.currentCase.depositions ?? []}
                                onDepositionsChange={props.onDepositionsChange}
                            />
                        </td>
                    </tr>
                );
            }
            break;
        case CaseFileStaticItems.ExpertsSection.Id:
            if (showExpertsSection) {
                secondaryRow = (
                    <tr>
                        <td colSpan={3}>
                            <CaseExpertsList
                                user={user}
                                caseExperts={props.currentCase.caseExperts ?? []}
                                caseGuid={props.currentCase.guid}
                                refreshParent={props.onExpertsChanges}
                                onSetCurrentCaseExpert={props.onSetCurrentCaseExpert}
                            />
                        </td>
                    </tr>
                );
            }
            break;
        case CaseFileStaticItems.DocumentsSection.Id:
            if (showDocumentsSection) {
                const canEditCaseFile = Authorization.userHasRight(
                    UserRightsEnum.EditCaseFile,
                    user,
                    zoneContext.zoneGuid
                );

                secondaryRow = (
                    <tr>
                        <td colSpan={3}>
                            {canEditCaseFile && props.currentCase.guid && (
                                <DocumentsList
                                    authorizedToEdit={canEditCaseFile}
                                    documents={props.currentCase.documents}
                                    documentTypeId={DocumentTypeEnum.Case}
                                    entityGuid={props.currentCase.guid}
                                    entityTypeId={EntityTypes.Case}
                                    uploadOnly={true}
                                    user={user}
                                    onSaveComplete={props.onDocumentSaveComplete}
                                    onDeleteComplete={props.onDocumentDeleteComplete}
                                />
                            )}
                        </td>
                    </tr>
                );
            }
            break;
        default:
            break;
    }

    return (
        <>
            {primaryRow}
            {secondaryRow}
        </>
    );
}
